import React from 'react';
import {
  Box, Flex, Grid, GridItem, Text, Stat, StatLabel, StatNumber,
  StatHelpText, StatArrow, Icon, useColorModeValue, VStack,
  HStack, Avatar, Heading, Card, CardHeader, CardBody, Progress,
  Badge
} from '@chakra-ui/react';
import {
  MdPeople, MdAttachMoney, MdTrendingUp, MdPhone,
  MdEmail, MdCheckCircle, MdSchedule, MdCancel
} from 'react-icons/md';
import ComercialSidebar from '../Sidebar/ComercialSidebar';
import './ComercialDashboard.css';

const StatCard = ({ icon, label, value, change, changeType, colorScheme }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
      transition="all 0.3s"
      _hover={{ shadow: 'md' }}
    >
      <Stat>
        <Flex justify="space-between" align="flex-start">
          <Box>
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" mt={2}>{value}</StatNumber>
            {change && (
              <StatHelpText mt={2}>
                <StatArrow type={changeType} />
                {change}
              </StatHelpText>
            )}
          </Box>
          <Icon as={icon} fontSize="40px" color={iconColor} />
        </Flex>
      </Stat>
    </Box>
  );
};

const ComercialDashboard = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');

  return (
    <Flex minH="100vh" bg={bg}>
      <ComercialSidebar />
      
      <Box
        flex="1"
        ml={{ base: 0, lg: "250px" }}
        transition="margin-left 0.3s"
      >
        <Box p={{ base: 4, md: 8 }}>
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" color={textColor}>
                Dashboard Comercial
              </Heading>
              <Text color="gray.500" mt={1}>
                Bem-vindo de volta, {userInfo?.name || 'Comercial'}!
              </Text>
            </Box>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={userInfo?.name || 'Comercial'}
                src={userInfo?.avatar || ''}
                bg="blue.500"
              />
            </HStack>
          </Flex>

          {/* Stats Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            mb={8}
          >
            <GridItem>
              <StatCard
                icon={MdPeople}
                label="Novos Leads"
                value="48"
                change="15% esta semana"
                changeType="increase"
                colorScheme="blue"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdAttachMoney}
                label="Propostas Enviadas"
                value="23"
                change="8% este mês"
                changeType="increase"
                colorScheme="green"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdPhone}
                label="Ligações Realizadas"
                value="127"
                change="12% hoje"
                changeType="increase"
                colorScheme="purple"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdTrendingUp}
                label="Taxa de Conversão"
                value="32%"
                change="3% este mês"
                changeType="decrease"
                colorScheme="orange"
              />
            </GridItem>
          </Grid>

          {/* Pipeline and Activities */}
          <Grid
            templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
            gap={6}
          >
            {/* Pipeline Status */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Pipeline de Vendas</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm">Prospecção</Text>
                        <Text fontSize="sm" fontWeight="bold">12 leads</Text>
                      </HStack>
                      <Progress value={30} colorScheme="blue" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm">Qualificação</Text>
                        <Text fontSize="sm" fontWeight="bold">8 leads</Text>
                      </HStack>
                      <Progress value={20} colorScheme="cyan" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm">Proposta</Text>
                        <Text fontSize="sm" fontWeight="bold">5 leads</Text>
                      </HStack>
                      <Progress value={12} colorScheme="orange" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm">Negociação</Text>
                        <Text fontSize="sm" fontWeight="bold">3 leads</Text>
                      </HStack>
                      <Progress value={8} colorScheme="purple" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm">Fechamento</Text>
                        <Text fontSize="sm" fontWeight="bold">2 leads</Text>
                      </HStack>
                      <Progress value={5} colorScheme="green" borderRadius="full" />
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Recent Activities */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Atividades Recentes</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <HStack>
                      <Icon as={MdEmail} color="blue.500" fontSize="20px" />
                      <Box flex="1">
                        <Text fontWeight="medium" fontSize="sm">E-mail enviado</Text>
                        <Text fontSize="xs" color="gray.500">João Silva - Proposta #2341</Text>
                      </Box>
                      <Badge colorScheme="blue">Há 10 min</Badge>
                    </HStack>
                    <HStack>
                      <Icon as={MdPhone} color="green.500" fontSize="20px" />
                      <Box flex="1">
                        <Text fontWeight="medium" fontSize="sm">Ligação realizada</Text>
                        <Text fontSize="xs" color="gray.500">Maria Santos - Follow-up</Text>
                      </Box>
                      <Badge colorScheme="green">Há 30 min</Badge>
                    </HStack>
                    <HStack>
                      <Icon as={MdCheckCircle} color="purple.500" fontSize="20px" />
                      <Box flex="1">
                        <Text fontWeight="medium" fontSize="sm">Proposta aceita</Text>
                        <Text fontSize="xs" color="gray.500">Pedro Costa - R$ 45.000</Text>
                      </Box>
                      <Badge colorScheme="purple">Há 2 horas</Badge>
                    </HStack>
                    <HStack>
                      <Icon as={MdSchedule} color="orange.500" fontSize="20px" />
                      <Box flex="1">
                        <Text fontWeight="medium" fontSize="sm">Reunião agendada</Text>
                        <Text fontSize="xs" color="gray.500">Ana Lima - Amanhã 14:00</Text>
                      </Box>
                      <Badge colorScheme="orange">Há 3 horas</Badge>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </Box>
      </Box>
    </Flex>
  );
};

export default ComercialDashboard;
